import { ApplicationRef, ComponentRef, EnvironmentInjector, Injectable, createComponent, inject } from "@angular/core";
import { ImagePreviewComponent } from "../components/image-preview/image-preview.component";
import { ImagePreviewConfig } from "../interfaces/image-preview.interfaces";

/**
 * Service that opens and closes an image preview overlay by dynamically creating
 * {@link ImagePreviewComponent} and attaching it to the Angular application.
 *
 * Guarantees a single active instance — calling {@link open} closes any existing preview first.
 */
@Injectable()
export class ImagePreviewService {
  private readonly appRef = inject(ApplicationRef);
  private readonly envInjector = inject(EnvironmentInjector);
  private cmpRef?: ComponentRef<ImagePreviewComponent>;

  open(config: ImagePreviewConfig, hostEl: HTMLElement = document.body): ComponentRef<ImagePreviewComponent> {
    this.close();

    const ref = createComponent(ImagePreviewComponent, {
      environmentInjector: this.envInjector,
    });
    this.cmpRef = ref;

    const openedSub = ref.instance.opened.subscribe(opened => {
      if (!opened) this.close();
    });

    ref.onDestroy(() => {
      openedSub.unsubscribe();
      if (this.cmpRef === ref) this.dispose();
    });

    if (config.file !== undefined) ref.setInput("file", config.file);
    if (config.imageAlt !== undefined) ref.setInput("imageAlt", config.imageAlt);
    if (config.resourcePipeOptions !== undefined) ref.setInput("resourcePipeOptions", config.resourcePipeOptions);
    if (config.resolveUrl !== undefined) ref.setInput("resolveUrl", config.resolveUrl);
    if (config.minZoom !== undefined) ref.setInput("minZoom", config.minZoom);
    if (config.maxZoom !== undefined) ref.setInput("maxZoom", config.maxZoom);
    if (config.zoomFactor !== undefined) ref.setInput("zoomFactor", config.zoomFactor);

    ref.setInput("opened", true);

    this.appRef.attachView(ref.hostView);

    const effectiveHost = hostEl?.isConnected ? hostEl : document.body;
    effectiveHost.appendChild(ref.location.nativeElement);

    ref.changeDetectorRef.detectChanges();

    return ref;
  }

  close(): void {
    this.dispose();
  }

  private dispose(): void {
    if (!this.cmpRef) return;

    this.appRef.detachView(this.cmpRef.hostView);
    this.cmpRef.destroy();
    this.cmpRef = undefined;
  }
}
